import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import {
  EmptyState, PageLoading, SearchInput,
} from '../../components/ui';

interface PortalRetailer {
  id: string;
  name: string;
  code: string | null;
  route: { id: string; name: string } | null;
  status: string;
  createdAt: string;
}

/**
 * The distributor's retailer list.
 *
 * Every retailer assigned to this distributor — whether by area or
 * by a per-customer override — appears here. The list is read-only
 * for the distributor; the admin manages assignments.
 */
export default function DistributorRetailers() {
  const toast = useToast();
  const [retailers, setRetailers] = useState<PortalRetailer[] | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ customers: PortalRetailer[]; nextCursor: string | null }>('/distributor/retailers');
      setRetailers(res.customers);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load retailers.');
      setRetailers([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (retailers ?? []).filter((r) =>
      !needle
      || r.name.toLowerCase().includes(needle)
      || r.route?.name.toLowerCase().includes(needle));
  }, [retailers, search]);

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Retailers</h1>
          <p className="topbar-sub">
            {retailers
              ? `${retailers.length} retailer${retailers.length === 1 ? '' : 's'} in your territory`
              : 'Loading…'}
          </p>
        </div>
      </header>

      <div className="page col gap-16">
        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search retailers" />
        </div>

        {!retailers ? <PageLoading /> : visible.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="users"
              title={search ? 'No retailers match that' : 'No retailers assigned'}
              body={search
                ? 'Try a different name or area.'
                : 'Retailers are assigned to you based on area. Contact your company admin to get areas assigned.'}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Retailer</th>
                    <th>Code</th>
                    <th>Area</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.id}>
                      <td className="primary">{r.name}</td>
                      <td className="mono small">{r.code ?? '—'}</td>
                      <td>{r.route?.name ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
